<template>
  <div>
    <v-toolbar elevation="0" class="mb-3 px-2" rounded>
      <v-btn icon variant="text" to="/console/webhook-integrations"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <div class="text-h6 ml-2">{{ integration?.name || $t('routes.webhookIntegrations.webhookIntegrations') }}</div>
      <v-spacer />
      <WebhookEndpointDialog :node-id="accessPointId" :integration-id="integrationId" @saved="onEndpointSaved">
        <v-btn class="bg-primary" elevation="0">
          <v-icon>mdi-plus</v-icon>
          <span class="hidden-sm-and-down">{{ $t('routes.webhookIntegrations.createEndpoint') }}</span>
        </v-btn>
      </WebhookEndpointDialog>
    </v-toolbar>

    <WebhookUsageHelp />

    <v-card v-if="integration" class="mb-4" variant="tonal">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6"><strong>{{ $t('routes.webhookIntegrations.type') }}:</strong> {{ integrationTypeLabel }}</v-col>
          <v-col cols="12" md="6"><strong>{{ $t('routes.webhookIntegrations.status') }}:</strong> {{ integrationStateLabel }}</v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-tabs v-model="activeTab" class="mb-3">
      <v-tab value="endpoints">{{ $t('routes.webhookIntegrations.endpoints') }}</v-tab>
      <v-tab value="deliveries">{{ $t('routes.webhookIntegrations.deliveryHistory') }}</v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <v-window-item value="endpoints">
        <v-data-iterator :items="endpoints" :items-per-page="20" :loading="loading" return-object>
          <template v-slot:no-data><v-row class="mt-6"><v-col class="text-center">{{ $t('routes.webhookIntegrations.noEndpointsFound') }}</v-col></v-row></template>
          <template v-slot:default="{ items }">
            <v-card v-for="item in items" :key="item.value.id" class="mb-2">
              <v-card-text>
                <div class="d-flex align-start">
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">{{ item.value.name }}</div>
                    <div class="text-caption text-grey">{{ item.value.url }}</div>
                    <div class="mt-2">
                      <v-chip :color="getStateColor(item.value.state)" size="small" variant="tonal" class="mr-2">{{ $t(`routes.webhookIntegrations.states.${item.value.state}`) }}</v-chip>
                      <v-chip v-if="item.value.allow_team_only" size="small" variant="tonal" color="warning" class="mr-2">{{ $t('routes.webhookIntegrations.allowTeamOnly') }}</v-chip>
                      <v-chip v-for="eventType in item.value.event_subscriptions" :key="eventType" size="small" variant="tonal" class="mr-1 mb-1">{{ $t(`routes.webhookIntegrations.events.${getEventTranslationKey(eventType)}`) }}</v-chip>
                    </div>
                    <div class="text-caption mt-2">
                      {{ $t('routes.webhookIntegrations.consecutiveFailures') }}: {{ item.value.consecutive_failures || 0 }}
                    </div>
                    <WebhookEndpointDeliverySummary
                      :summary="getEndpointDeliverySummary(item.value.id)"
                      :loading="isEndpointSummaryLoading(item.value.id)"
                    />
                  </div>
                  <div>
                    <v-menu>
                      <template v-slot:activator="{ props }"><v-btn icon variant="text" v-bind="props"><v-icon>mdi-dots-vertical</v-icon></v-btn></template>
                      <v-list>
                        <v-list-item @click="openEditEndpoint(item.value)"><template v-slot:prepend><v-icon size="small">mdi-pencil-outline</v-icon></template><v-list-item-title>{{ $t('routes.webhookIntegrations.edit') }}</v-list-item-title></v-list-item>
                        <v-list-item v-if="item.value.state !== 'active'" @click="requestEndpointAction('resume', item.value, 'active')"><template v-slot:prepend><v-icon size="small">mdi-play-circle-outline</v-icon></template><v-list-item-title>{{ $t('routes.webhookIntegrations.resume') }}</v-list-item-title></v-list-item>
                        <v-list-item v-if="item.value.state === 'active'" @click="requestEndpointAction('pause', item.value, 'paused')"><template v-slot:prepend><v-icon size="small">mdi-pause-circle-outline</v-icon></template><v-list-item-title>{{ $t('routes.webhookIntegrations.pause') }}</v-list-item-title></v-list-item>
                        <v-list-item v-if="item.value.state !== 'disabled'" @click="requestEndpointAction('disable', item.value, 'disabled')"><template v-slot:prepend><v-icon size="small">mdi-cancel</v-icon></template><v-list-item-title>{{ $t('routes.webhookIntegrations.disable') }}</v-list-item-title></v-list-item>
                        <v-list-item @click="requestEndpointAction('sendTest', item.value)"><template v-slot:prepend><v-icon size="small">mdi-send-outline</v-icon></template><v-list-item-title>{{ $t('routes.webhookIntegrations.sendTestWebhook') }}</v-list-item-title></v-list-item>
                        <v-list-item @click="requestEndpointAction('rotateSecret', item.value)"><template v-slot:prepend><v-icon size="small">mdi-key-outline</v-icon></template><v-list-item-title>{{ $t('routes.webhookIntegrations.rotateSigningSecret') }}</v-list-item-title></v-list-item>
                        <v-divider />
                        <v-list-item @click="requestEndpointAction('delete', item.value)"><template v-slot:prepend><v-icon size="small" color="error">mdi-delete-outline</v-icon></template><v-list-item-title class="text-error">{{ $t('routes.webhookIntegrations.delete') }}</v-list-item-title></v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </template>
        </v-data-iterator>
      </v-window-item>

      <v-window-item value="deliveries">
        <WebhookDeliveriesList
          :deliveries="deliveries"
          :loading="deliveriesLoading"
          :status-filter="deliveryStatusFilter"
          :event-type-filter="deliveryEventTypeFilter"
          :redelivering-delivery-id="redeliveringDeliveryId"
          @refresh="fetchDeliveries"
          @update:statusFilter="updateDeliveryStatusFilter"
          @update:eventTypeFilter="updateDeliveryEventTypeFilter"
          @view="openDeliveryDetails"
          @redeliver="redeliverDelivery"
        />
      </v-window-item>
    </v-window>

    <WebhookEndpointDialog v-if="editingEndpoint" v-model="showEditEndpointDialog" :endpoint="editingEndpoint" :node-id="accessPointId" :integration-id="integrationId" @saved="onEndpointUpdated" />
    <WebhookDeliveryDetailsDialog
      v-model="showDeliveryDetailsDialog"
      :delivery="selectedDeliveryDetails"
      :loading="deliveryDetailsLoading"
      :error-message="deliveryDetailsError"
    />
    <WebhookConfirmDialog
      v-model="showEndpointActionDialog"
      :title="endpointActionTitle"
      :message="endpointActionMessage"
      :confirm-label="endpointActionConfirmLabel"
      :color="endpointActionColor"
      :danger="isDeletingEndpoint"
      :loading="endpointActionLoading"
      @confirm="confirmEndpointAction"
    />
    <WebhookSecretDialog v-model="showSecretDialog" :secret="signingSecret" />
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useWebhookIntegrationStore } from '@/stores/WebhookIntegrationStore.js';
import { getWebhookApiErrorMessage } from '@/routes/console/webhook-integrations/WebhookApiError.js';
import WebhookDeliveriesList from '@/routes/console/webhook-integrations/components/WebhookDeliveriesList.vue';
import WebhookDeliveryDetailsDialog from '@/routes/console/webhook-integrations/components/dialogs/WebhookDeliveryDetailsDialog.vue';
import WebhookEndpointDeliverySummary from '@/routes/console/webhook-integrations/components/WebhookEndpointDeliverySummary.vue';
import WebhookConfirmDialog from '@/routes/console/webhook-integrations/components/dialogs/WebhookConfirmDialog.vue';
import WebhookUsageHelp from '@/routes/console/webhook-integrations/components/WebhookUsageHelp.vue';
import WebhookEndpointDialog from './components/dialogs/WebhookEndpointDialog.vue';
import WebhookSecretDialog from './components/dialogs/WebhookSecretDialog.vue';
import { formatWebhookType, getEventTranslationKey, getStateColor } from './WebhookConstants.js';

export default {
  name: 'WebhookIntegrationDetails',
  components: {
    WebhookDeliveriesList,
    WebhookDeliveryDetailsDialog,
    WebhookEndpointDeliverySummary,
    WebhookConfirmDialog,
    WebhookUsageHelp,
    WebhookEndpointDialog,
    WebhookSecretDialog,
  },
  data() {
    return {
      activeTab: 'endpoints',
      loading: true,
      deliveriesLoading: false,
      deliveryDetailsLoading: false,
      deliveryDetailsError: '',
      redeliveringDeliveryId: null,
      deliveryStatusFilter: null,
      deliveryEventTypeFilter: null,
      selectedDeliveryId: null,
      endpointSummaryLoadingById: {},
      endpointActionLoading: false,
      pendingEndpointAction: null,
      editingEndpoint: null,
      showEditEndpointDialog: false,
      showDeliveryDetailsDialog: false,
      showEndpointActionDialog: false,
      showSecretDialog: false,
      signingSecret: '',
    };
  },
  mounted() { this.fetchData(); },
  computed: {
    accessPointId() { return useAuthStore().rootNodeId; },
    integrationId() { return this.$route.params.integrationId; },
    integration() { return useWebhookIntegrationStore().getIntegrations.find((item) => item.id === this.integrationId) || null; },
    integrationTypeLabel() {
      const type = useWebhookIntegrationStore().getIntegrationTypes.find((item) => item.id === this.integration?.type);
      return type?.label || formatWebhookType(this.integration?.type) || '-';
    },
    integrationStateLabel() {
      return this.integration?.state ? this.$t(`routes.webhookIntegrations.states.${this.integration.state}`) : '-';
    },
    endpoints() { return useWebhookIntegrationStore().getEndpoints(this.integrationId); },
    deliveries() { return useWebhookIntegrationStore().getIntegrationDeliveries(this.integrationId); },
    selectedDeliveryDetails() { return useWebhookIntegrationStore().getDeliveryDetail(this.selectedDeliveryId); },
    endpointActionTitle() {
      return this.getEndpointActionTranslation('Title');
    },
    endpointActionMessage() {
      return this.getEndpointActionTranslation('Message');
    },
    endpointActionConfirmLabel() {
      return this.getEndpointActionTranslation('Confirm');
    },
    endpointActionColor() {
      return ['delete', 'disable'].includes(this.pendingEndpointAction?.action) ? 'error' : 'warning';
    },
    isDeletingEndpoint() {
      return this.pendingEndpointAction?.action === 'delete';
    },
  },
  methods: {
    getEventTranslationKey,
    getStateColor,
    async fetchData() {
      this.loading = true;
      try {
        const store = useWebhookIntegrationStore();
        if (store.getIntegrations.length === 0) await store.fetchIntegrations({ nodeId: this.accessPointId });
        const endpoints = await store.fetchEndpoints({ nodeId: this.accessPointId, integrationId: this.integrationId });
        await Promise.all([
          this.fetchEndpointSummaries(endpoints),
          this.fetchDeliveries(),
        ]);
      } catch {
        this.$showMessage(this.$t('routes.webhookIntegrations.fetchEndpointsError'), { type: 'error' });
      } finally { this.loading = false; }
    },
    onEndpointSaved(endpoint) {
      this.$showMessage(this.$t('routes.webhookIntegrations.endpointCreated'));
      if (endpoint.signing_secret) this.showSecret(endpoint.signing_secret);
      this.fetchEndpointSummary(endpoint.id);
    },
    openEditEndpoint(endpoint) { this.editingEndpoint = endpoint; this.showEditEndpointDialog = true; },
    onEndpointUpdated(endpoint) {
      this.showEditEndpointDialog = false;
      this.editingEndpoint = null;
      this.$showMessage(this.$t('routes.webhookIntegrations.endpointUpdated'));
      this.fetchEndpointSummary(endpoint.id);
    },
    showSecret(secret) { this.signingSecret = secret; this.showSecretDialog = true; },
    getEndpointDeliverySummary(endpointId) {
      return useWebhookIntegrationStore().getEndpointDeliverySummary(this.integrationId, endpointId);
    },
    isEndpointSummaryLoading(endpointId) {
      return Boolean(this.endpointSummaryLoadingById[endpointId]);
    },
    async fetchEndpointSummaries(endpoints) {
      await Promise.all(endpoints.map((endpoint) => this.fetchEndpointSummary(endpoint.id)));
    },
    async fetchEndpointSummary(endpointId) {
      this.endpointSummaryLoadingById = { ...this.endpointSummaryLoadingById, [endpointId]: true };
      try {
        await useWebhookIntegrationStore().fetchEndpointDeliverySummary({
          nodeId: this.accessPointId,
          integrationId: this.integrationId,
          endpointId,
          limit: 10,
        });
      } catch (error) {
        console.error('Error fetching webhook endpoint delivery summary', error);
      } finally {
        this.endpointSummaryLoadingById = { ...this.endpointSummaryLoadingById, [endpointId]: false };
      }
    },
    async fetchDeliveries() {
      this.deliveriesLoading = true;
      try {
        await useWebhookIntegrationStore().fetchIntegrationDeliveries({
          nodeId: this.accessPointId,
          integrationId: this.integrationId,
          status: this.deliveryStatusFilter,
          eventType: this.deliveryEventTypeFilter,
          limit: 50,
        });
      } catch (error) {
        console.error('Error fetching webhook deliveries', error);
        this.$showMessage(this.$t('routes.webhookIntegrations.fetchDeliveriesError'), { type: 'error' });
      } finally {
        this.deliveriesLoading = false;
      }
    },
    updateDeliveryStatusFilter(status) {
      this.deliveryStatusFilter = status;
      this.fetchDeliveries();
    },
    updateDeliveryEventTypeFilter(eventType) {
      this.deliveryEventTypeFilter = eventType;
      this.fetchDeliveries();
    },
    async openDeliveryDetails(delivery) {
      this.selectedDeliveryId = delivery.id;
      this.deliveryDetailsError = '';
      this.showDeliveryDetailsDialog = true;
      this.deliveryDetailsLoading = true;
      try {
        await useWebhookIntegrationStore().fetchEndpointDelivery({
          nodeId: this.accessPointId,
          integrationId: this.integrationId,
          endpointId: delivery.webhook_endpoint_id,
          deliveryId: delivery.id,
        });
      } catch (error) {
        console.error('Error fetching webhook delivery details', error);
        this.deliveryDetailsError = this.$t('routes.webhookIntegrations.fetchDeliveryDetailsError');
      } finally {
        this.deliveryDetailsLoading = false;
      }
    },
    async redeliverDelivery(delivery) {
      this.redeliveringDeliveryId = delivery.id;
      try {
        await useWebhookIntegrationStore().redeliverDelivery({
          nodeId: this.accessPointId,
          integrationId: this.integrationId,
          endpointId: delivery.webhook_endpoint_id,
          deliveryId: delivery.id,
        });
        this.$showMessage(this.$t('routes.webhookIntegrations.deliveryRedelivered'));
        await Promise.all([
          this.fetchDeliveries(),
          this.fetchEndpointSummary(delivery.webhook_endpoint_id),
        ]);
      } catch (error) {
        const fallbackMessage = this.$t('routes.webhookIntegrations.redeliverDeliveryError');
        this.$showMessage(getWebhookApiErrorMessage(error, fallbackMessage), { type: 'error' });
      } finally {
        this.redeliveringDeliveryId = null;
      }
    },
    getEndpointActionTranslation(suffix) {
      const action = this.pendingEndpointAction?.action || 'pause';
      return this.$t(`routes.webhookIntegrations.endpointActions.${action}${suffix}`);
    },
    requestEndpointAction(action, endpoint, state = null) {
      this.pendingEndpointAction = { action, endpoint, state };
      this.showEndpointActionDialog = true;
    },
    async confirmEndpointAction() {
      if (!this.pendingEndpointAction || this.endpointActionLoading) return;
      const { action, endpoint, state } = this.pendingEndpointAction;
      this.endpointActionLoading = true;
      try {
        if (['pause', 'resume', 'disable'].includes(action)) await this.updateEndpointState(endpoint, state);
        if (action === 'sendTest') await this.sendTest(endpoint);
        if (action === 'rotateSecret') await this.rotateSecret(endpoint);
        if (action === 'delete') await this.deleteEndpoint(endpoint);
        this.showEndpointActionDialog = false;
        this.pendingEndpointAction = null;
      } finally {
        this.endpointActionLoading = false;
      }
    },
    async updateEndpointState(endpoint, state) {
      try {
        await useWebhookIntegrationStore().updateEndpointState({ nodeId: this.accessPointId, integrationId: this.integrationId, endpointId: endpoint.id, state });
        this.$showMessage(this.$t('routes.webhookIntegrations.endpointUpdated'));
        await this.fetchEndpointSummary(endpoint.id);
      } catch (error) {
        const fallbackMessage = this.$t('routes.webhookIntegrations.endpointStateError');
        this.$showMessage(getWebhookApiErrorMessage(error, fallbackMessage), { type: 'error' });
      }
    },
    async sendTest(endpoint) {
      try {
        await useWebhookIntegrationStore().sendTest({ nodeId: this.accessPointId, integrationId: this.integrationId, endpointId: endpoint.id });
        this.$showMessage(this.$t('routes.webhookIntegrations.testWebhookQueued'));
        await Promise.all([this.fetchDeliveries(), this.fetchEndpointSummary(endpoint.id)]);
      } catch (error) {
        const fallbackMessage = this.$t('routes.webhookIntegrations.sendTestWebhookError');
        this.$showMessage(getWebhookApiErrorMessage(error, fallbackMessage), { type: 'error' });
      }
    },
    async rotateSecret(endpoint) {
      try {
        const result = await useWebhookIntegrationStore().rotateSigningSecret({ nodeId: this.accessPointId, integrationId: this.integrationId, endpointId: endpoint.id });
        this.showSecret(result.signing_secret);
      } catch (error) {
        const fallbackMessage = this.$t('routes.webhookIntegrations.rotateSigningSecretError');
        this.$showMessage(getWebhookApiErrorMessage(error, fallbackMessage), { type: 'error' });
      }
    },
    async deleteEndpoint(endpoint) {
      try {
        await useWebhookIntegrationStore().deleteEndpoint({ nodeId: this.accessPointId, integrationId: this.integrationId, endpointId: endpoint.id });
        this.$showMessage(this.$t('routes.webhookIntegrations.endpointDeleted'));
        await this.fetchDeliveries();
      } catch (error) {
        const fallbackMessage = this.$t('routes.webhookIntegrations.deleteEndpointError');
        this.$showMessage(getWebhookApiErrorMessage(error, fallbackMessage), { type: 'error' });
      }
    },
  },
}
</script>
